"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Mail, Phone, MapPin, Loader, CircleCheck, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function StartYourBuild() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="start-your-build" className="dark bg-[#0f0f10] text-white py-20 lg:py-32 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div ref={ref} className={cn("flex flex-col gap-8 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
            <div className="space-y-4">
              <h2 className="text-4xl font-bold tracking-tight text-white ![margin:35px_0px_16px_0px] md:text-5xl">
                Let's Build <span className="text-primary">Something Great.</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                Ready to take your business online? Fill out the form to get started with your new professional website. We'll get back to you within 24 hours.
              </p>
            </div>

            <div className="space-y-6 mt-4">
              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-full bg-secondary/50 border border-border group-hover:border-primary/50 group-hover:bg-primary/10 transition-colors">
                  <Phone className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Phone</h3>
                  <Link to="tel:0473462554" className="text-muted-foreground hover:text-primary transition-colors">0473 462 554</Link>
                  <p className="text-sm text-muted-foreground/60 mt-1">
                    Mon-Fri 9am-5pm
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-full bg-secondary/50 border border-border group-hover:border-primary/50 group-hover:bg-primary/10 transition-colors">
                  <Mail className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Email</h3>
                  <Link to="mailto:Admin@websiteshop.com.au" className="text-muted-foreground hover:text-primary transition-colors">
                    Admin@websiteshop.com.au
                  </Link>
                  <p className="text-sm text-muted-foreground/60 mt-1">Online support</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-full bg-secondary/50 border border-border group-hover:border-primary/50 group-hover:bg-primary/10 transition-colors">
                  <MapPin className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Location</h3>
                  <p className="text-muted-foreground">
                    Central Coast, Australia
                  </p>
                  <p className="text-sm text-muted-foreground/60 mt-1">
                    Serving businesses Australia-wide
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-2xl p-6 md:p-8 shadow-2xl">
            <FormProvider formId="start_build_form_contact" sectionName="start-your-build" defaultValues={{
            name: "",
            email: "",
            phone: "",
            business_name: "",
            package: "",
            details: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            business_name: {
              required: true
            },
            package: {
              required: true
            },
            details: {
              required: false
            }
          }} submitText="Start My Build" successMessage="Thanks! We've received your details and will be in touch shortly to discuss your project." errorMessage="Something went wrong. Please try again or contact us directly." className="w-full">
              <BuildForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function BuildForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 bg-primary/10 rounded-full flex items-center justify-center mb-2">
          <CircleCheck className="size-10 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-white">Message Sent!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-full border-primary/20 hover:bg-primary/10 hover:text-primary">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-secondary/50 border-border focus-visible:border-primary focus-visible:ring-primary/20 h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="business_name" rules={{
        required: "Business name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">
                Business / Trade Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Acme Paints" className="bg-secondary/50 border-border focus-visible:border-primary focus-visible:ring-primary/20 h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-secondary/50 border-border focus-visible:border-primary focus-visible:ring-primary/20 h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+61 2 1234 5678" className="bg-secondary/50 border-border focus-visible:border-primary focus-visible:ring-primary/20 h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="package" rules={{
      required: "Please select a package"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Select Package</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="bg-secondary/50 border-border focus:border-primary focus:ring-primary/20 h-12">
                  <SelectValue placeholder="Choose a package" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Starter - $995 + GST (+ $39/month hosting)">
                  Starter - $995 + GST (+ $39/month hosting)
                </SelectItem>
                <SelectItem value="Advanced - $1,400 + GST (+ $39/month hosting)">
                  Advanced - $1,400 + GST (+ $39/month hosting)
                </SelectItem>
                <SelectItem value="Content Creation Add-On - $795 + GST">
                  Content Creation Add-On - $795 + GST
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">
              Project Details / Questions
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your project, timeline, or any specific requirements..." className="bg-secondary/50 border-border focus-visible:border-primary focus-visible:ring-primary/20 min-h-[120px] resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg animate-in fade-in slide-in-from-top-2">
          <p className="text-destructive text-sm font-medium text-center">
            {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 rounded-full text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all duration-300 mt-2">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : <>
            Start My Build <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}